'use client';

import { useCallback, useEffect, useRef } from 'react';

import {
  useApolloClient,
  useMutation,
  useQuery,
  useSubscription,
} from '@apollo/client/react';

import { CreateLiveDocument, NewHitDocument } from './Event.mutation.generated';
import {
  EventGetInfoDocument,
  EventGetLiveDocument,
  LiveGetPostsDocument,
} from './Event.query.generated';
import {
  EventListenInfosDocument,
  LiveListenInfosDocument,
} from './Event.subscription.generated';
import { useEventPostsAutoRefresh } from './useEventPosts';

export const useEventLive = ({
  userId = 0,
  eventId,
  generateLive = true,
}: {
  userId?: number;
  eventId: number;
  generateLive?: boolean;
}) => {
  const eventQuery = useQuery(EventGetInfoDocument, {
    context: {
      role: 'anonymous',
    },
    variables: { eventId },
    skip: !eventId,
  });
  const liveQuery = useQuery(EventGetLiveDocument, {
    context: {
      role: 'anonymous',
    },
    fetchPolicy: 'cache-and-network',
    skip: !eventId,
    variables: { eventId, userId },
  });

  const [createLive, createLiveStatus] = useMutation(CreateLiveDocument, {
    onCompleted: () => {
      liveQuery.refetch();
    },
  });

  const [newHit] = useMutation(NewHitDocument, {
    context: {
      role: 'user',
    },
  });

  const refreshPosts = useEventPostsAutoRefresh({
    liveId: liveQuery.data?.lives_live?.[0]?.id,
  });

  const client = useApolloClient();

  useEffect(() => {
    if (
      generateLive &&
      !liveQuery.loading &&
      !liveQuery.data?.lives_live?.length &&
      eventQuery.data?.competitions_event_by_pk?.playing_teams?.length === 2
    ) {
      createLive({
        variables: {
          eventId,
          playingTeam1Id:
            eventQuery.data.competitions_event_by_pk.playing_teams[0].id,
          playingTeam2Id:
            eventQuery.data.competitions_event_by_pk.playing_teams[1].id,
          score1:
            eventQuery.data.competitions_event_by_pk.playing_teams[0].score,
          score2:
            eventQuery.data.competitions_event_by_pk.playing_teams[1].score,
          role1: eventQuery.data.competitions_event_by_pk.playing_teams[0]
            .role as string,
          role2: eventQuery.data.competitions_event_by_pk.playing_teams[1]
            .role as string,
          status: eventQuery.data.competitions_event_by_pk.status as string,
          video: '',
        },
      });
    }
  }, [
    liveQuery.loading,
    !!liveQuery.data?.lives_live?.length,
    !!eventQuery.data?.competitions_event_by_pk,
  ]);

  const lastPostIdForLiveId = liveQuery.data?.lives_live?.[0]?.id;
  const isFirstExecutionForLiveId = useRef<{ [key: string]: boolean }>({});
  const skipLiveListenInfosSub = !(
    lastPostIdForLiveId &&
    !!client.readQuery({
      query: LiveGetPostsDocument,
      variables: {
        liveId: Number(lastPostIdForLiveId),
        limit: 1,
      },
    })
  );
  const LiveListenInfosSub = useSubscription(LiveListenInfosDocument, {
    variables: { liveId: Number(liveQuery.data?.lives_live?.[0]?.id) },
    skip: skipLiveListenInfosSub,
    onData: ({ data }) => {
      if (data) {
        const currentLiveId = String(liveQuery.data?.lives_live?.[0]?.id);

        if (isFirstExecutionForLiveId.current[currentLiveId] === undefined) {
          isFirstExecutionForLiveId.current[currentLiveId] = true;
        }

        if (isFirstExecutionForLiveId.current[currentLiveId]) {
          isFirstExecutionForLiveId.current[currentLiveId] = false;
          return;
        }
        refreshPosts.execute();
      }
    },
  });

  const isFirstEventListenExecution = useRef(true);
  const EventListenInfosSub = useSubscription(EventListenInfosDocument, {
    variables: { eventId: Number(eventId) },
    skip: !eventId,
    onData: ({ data }) => {
      if (data) {
        if (isFirstEventListenExecution.current) {
          isFirstEventListenExecution.current = false;
          return;
        }
        liveQuery.refetch();
        client?.refetchQueries({
          include: ['MobileGetEventData'],
        });
      }
    },
  });

  const newHitExecuted = useRef(false);
  useEffect(() => {
    if (!newHitExecuted.current && eventId) {
      newHit({
        variables: {
          eventId: Number(eventId),
        },
      });
      newHitExecuted.current = true;
    }
  }, [eventId]);

  const restartSubs = useCallback(() => {
    const currentLiveId = liveQuery.data?.lives_live?.[0]?.id;
    if (!currentLiveId) {
      return;
    }

    if (!skipLiveListenInfosSub && !LiveListenInfosSub.loading) {
      LiveListenInfosSub.restart();
    }
    if (eventId && !EventListenInfosSub.loading) {
      EventListenInfosSub.restart();
    }
    refreshPosts.execute();
    liveQuery.refetch();
    client?.refetchQueries({
      include: ['MobileGetEventData'],
    });
  }, [
    eventId,
    skipLiveListenInfosSub,
    LiveListenInfosSub,
    EventListenInfosSub,
    liveQuery.data?.lives_live,
  ]);

  return {
    ...liveQuery,
    loading:
      eventQuery.loading || createLiveStatus.loading || liveQuery.loading,
    error: eventQuery.error || createLiveStatus.error || liveQuery.error,
    restartSubs,
  };
};
